import { format, isSameYear } from 'date-fns';

import { proxyGet } from '../utils/proxy';
import zonedDate from '../utils/zoned-date';
import { sortBy } from 'lodash';

let _eventsPromise = null;

const now = zonedDate();

const CAPACITY_THRESHOLDS = {
    limited: {    // 10% of capacity,
        level: 0.1,
        text: 'Limited Seating',
    },
    sold_out: {
        level: 0,
        text: 'Sold Out',
    }
};

export function getKeywordCatogoryFilters() {
    return window.AppConfig.keywords.calendar_keywords.map(category => {
        return {
            categoryId: parseInt(category.category_id),
            displayTitle: category.title,
            key: `filter__${category.category_id}`,
        };
    });
}

/**
 *
 * @returns {[{categoryId: string}]}
 */
export function getKeywordTagCatogories() {
    return window.AppConfig.keywords.calendar_keywords.map(category => {
        return {
            categoryId: parseInt(category.category_id),
        };
    });
}

export function getEvents() {
    if (_eventsPromise) {
        return _eventsPromise;
    }

    _eventsPromise = Promise.all([
        proxyGet(`/event-instances.json`),
        proxyGet(`/events.json`),
    ])
        .then(([instances, events]) => {
            return instances.docs.map(instance => {
                instance = processKeywords(instance);
                const event = events.docs.find(event => event.url === instance.event.url);
                return processPerformance(instance, event);
            });
        });

    return _eventsPromise;
}

export function getPerformances() {
    return getEvents().then(response => {
        let performances = [];

        for (const bookableItem of response) {
            const data = {
                ...bookableItem,
            }

            data._auxiliary = {
                group_by_day: format(data.performancedate, 'yyyy-MM-dd'),
                group_by_month: format(data.performancedate, 'yyyy-MM'),
                group_name_month: isSameYear(now, data.performancedate) ? format(data.performancedate, 'MMMM') : format(data.performancedate, 'MMMM yyyy'),
            }

            performances.push(data);
        }

        performances = performances.filter(item => {
            return +zonedDate(item.performancedate) > +now;
        });

        return sortBy(performances, item => {
            return +item.performancedate;
        });
    });
}

/**
 * Mimic Shopify's handleize function
 *
 * https://github.com/Shopify/liquid/blob/42b6763546f218dc8ad1184e10c5e2d9a262b006/performance/shopify/shop_filter.rb#L97-L106
 */
function handleize(string) {
    if (!string) {
        return '';
    }

    return string
        .toString()
        .toLowerCase()
        .replace(/['"()[]]/g, '')
        .replace(/\W+/g, '-')
        .replace(/-+$/, '')
        .replace(/^-+/, '');
}

function getAvailabilityText(bookableItem) {
    let handle = bookableItem.attributes?.custom?.availability;

    if (handle === '(Hide Status)') {
        return null;
    }

    if (handle) {
        return handle;
    }

    const availability = parseFloat(bookableItem.tessitura?.availability, 10);
    const customThreshold = parseFloat(bookableItem?.event?.venue?.attributes?.custom?.limited_seating_threshold);

    // If the availability is not set, then we assume that the item is available
    if (!availability && bookableItem.tessitura?.availability) {
        return CAPACITY_THRESHOLDS.sold_out.text;
    }

    if (!bookableItem?.tessitura?.availability || !bookableItem?.event?.venue?.theme?.settings?.capacity) {
        return null;
    }

    const capacity = parseFloat(bookableItem.event.venue.theme.settings.capacity);

    if (customThreshold) {
        if (availability < customThreshold) {
            return CAPACITY_THRESHOLDS.limited.text;
        }
    }

    if (availability / capacity < CAPACITY_THRESHOLDS.limited.level) {
        return CAPACITY_THRESHOLDS.limited.text;
    }

    return null;
}

function processKeywords(bookableItem) {
    const categories = [];
    const eventTypes = [];
    const event = [];

    const eventTypesArray = bookableItem.attributes?.custom?.event_type ?? [];
    eventTypesArray.map(type => {
        categories.push(type);
    });

    event.push({ title: bookableItem.event.title });

    categories.length ? categories.map(category => {
        eventTypes.push({ title: category });
    }) : [];

    const keywordFilters = getKeywordCatogoryFilters().reduce((carry, filter) => {
        const categoryKeywords = (bookableItem.tessitura?.keywords || [])
            .filter(kw => kw.Category.Id === filter.categoryId)
            .map(kw => ({ title: kw.Description }));

        carry[filter.key] = categoryKeywords;

        return carry;
    }, {});

    bookableItem.tags = getKeywordTagCatogories().reduce((carry, cat) => {
        const tags = (bookableItem.tessitura?.keywords || [])
            .filter(kw => kw.Category.Id === cat.categoryId)
            .map(kw => (kw.Description));

        carry = carry.concat(tags);

        return carry;
    }, []);

    bookableItem.filters = {
        categories: categories,
        events_filters: event,
        event_type_filters: eventTypes,

        ...keywordFilters
    };

    return bookableItem;
}

function processPerformance(bookableItem, event = null) {
    bookableItem.venue = bookableItem.event.venue ? bookableItem.event.venue.name : null;
    bookableItem.title = bookableItem.event.title;
    bookableItem.prefix = bookableItem.event.prefix;
    // bookableItem.performancedate = zonedDate(bookableItem.startDate); // this is true UTC
    bookableItem.performancedate = zonedDate(bookableItem.startDateLocalAsUTC); // this is "local time" (i.e. ignore the timezone)
    bookableItem.description = bookableItem.event.description;
    bookableItem.image = bookableItem.event?.image || null;

    bookableItem.availability = getAvailabilityText(bookableItem);
    bookableItem.availability_handle = handleize(bookableItem.availability);

    if (bookableItem.availability !== CAPACITY_THRESHOLDS.sold_out.text) {
        // Primary CTA (will use theme CTA if set, otherwise till default to TNEW link & "Buy Tickets")
        if (event?.theme?.settings?.primaryBtn?.link) {
            bookableItem.ctaPrimary = {
                title: event?.theme?.settings?.primaryBtn?.title ?
                    event.theme.settings.primaryBtn.title :
                    'Buy Tickets',
                link: event.theme.settings.primaryBtn.link,
            };
        } else if (bookableItem.tessitura?.productionSeasonId && bookableItem.tessitura?.performanceId) {
            bookableItem.ctaPrimary = {
                title: 'Buy Tickets',
                link: `${AppConfig.tnewDomain}${bookableItem.tessitura.productionSeasonId}/${bookableItem.tessitura.performanceId}`,
            };
        }
    }

    if (bookableItem.ctaPrimary) {
        bookableItem.ctaPrimary.hideInCalendar = event?.theme?.settings?.primaryBtn ?
            event.theme.settings.primaryBtn.hideInCalendar :
            true;
    }

    // Secondary CTA (will use theme CTA if set, otherwise till default to Basker event link & "More Info")
    const secondaryLink = event?.theme?.settings?.secondaryBtn?.link ?
        event?.theme?.settings?.secondaryBtn?.link :
        bookableItem.event.url ?
            bookableItem.event.url :
            null;
    if (secondaryLink) {
        bookableItem.ctaSecondary = {
            title: event?.theme?.settings?.secondaryBtn?.title ?
                event?.theme?.settings?.secondaryBtn?.title :
                'More Info',
            link: secondaryLink,
            hideInCalendar: event?.theme?.settings?.secondaryBtn ?
                event.theme.settings.secondaryBtn.hideInCalendar :
                true,
        };
    }

    const date = bookableItem.performancedate;
    const hour = format(date, 'h');
    const minutes = format(date, 'mm');
    const period = format(date, 'a').toLowerCase();

    bookableItem.formattedTime = minutes === '00' ? `${hour}${period}` : `${hour}:${minutes}${period}`;

    return bookableItem;
}
